import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { getErrors, getIsLoading } from '@perpay-web/data-module/useDataModule';
import { passkeyAuthenticateBegin } from '@perpay-web/fintech/actions/passkey';
import { usePasskeyAuthenticateBeginContext } from '@perpay-web/fintech/hooks/dataModule/usePasskeyAuthenticateBeginContext';
import { usePasskeyAuthenticateCompleteContext } from '@perpay-web/fintech/hooks/dataModule/usePasskeyAuthenticateCompleteContext';
import { authenticateFaceId } from '@perpay-web/native-client/actions/biometricGate';
import { clearPasskeyIds } from '@perpay-web/native-client/actions/passkey';
import { useNativeSelector } from '@perpay-web/native-client/hooks/useNativeSelector';
import {
    getHasPasskey,
    getIsEnrolledWithBiometrics,
    getIsPasskeySupported,
    getBiometricsType,
    getIsNativePasskeyLaunched,
} from '@perpay-web/native-client/selectors/passkey';

import LoginWithBiometrics from './LoginWithBiometrics';

const LoginWithBiometricsContainer = ({ className, shouldPromptLogin }) => {
    const dispatch = useDispatch();
    const { state: authenticateBeginState } =
        usePasskeyAuthenticateBeginContext();
    const { state: authenticateCompleteState } =
        usePasskeyAuthenticateCompleteContext();

    const isLoading =
        getIsLoading(authenticateBeginState) ||
        getIsLoading(authenticateCompleteState);

    const errors = getErrors(authenticateBeginState);

    const hasPasskey = useNativeSelector(getHasPasskey);
    const isNativePasskeyLaunched = useNativeSelector(
        getIsNativePasskeyLaunched,
    );
    const onAuthenticate = useCallback(() => {
        dispatch(passkeyAuthenticateBegin());
    }, [dispatch]);

    const isPasskeySupported = useNativeSelector(getIsPasskeySupported);
    const isEnrolledWithBiometrics = useNativeSelector(
        getIsEnrolledWithBiometrics,
    );

    const shouldShowBiometricLogin =
        isPasskeySupported &&
        isNativePasskeyLaunched &&
        isEnrolledWithBiometrics;

    const biometricsType = useNativeSelector(getBiometricsType);

    const onToggleBiometrics = useCallback(
        (value) => {
            if (value) {
                dispatch(authenticateFaceId());
            }
            // If the user unchecks the box, even if they recheck it again,
            // it will stop prompting them to use a passkey to log in when they
            // land on the login page.
            // This is desirable because if the user deletes their passkey from the OS,
            // the app doesn't know and will continue to automatically prompt them with
            // the system passkey auth modal. Clearing the passkey IDs will allow
            // the app to know to stop prompting them.
            if (hasPasskey && !value) {
                dispatch(clearPasskeyIds());
            }
        },
        [dispatch, hasPasskey],
    );

    return (
        <LoginWithBiometrics
            shouldShowBiometricLogin={shouldShowBiometricLogin}
            onAuthenticate={onAuthenticate}
            errors={errors}
            className={className}
            biometricsType={biometricsType}
            isLoading={isLoading}
            isBiometricsRegistered={hasPasskey}
            onToggle={onToggleBiometrics}
            shouldPromptLogin={shouldPromptLogin}
        />
    );
};

export default LoginWithBiometricsContainer;
